<template>
  <div class="flex flex-col relative items-start justify-start pt-2 min-h-[400px] h-fit !overflow-auto pb-10 w-full" @keydown.enter.prevent>
    <Field v-if="!current_metric.id" v-slot="{ handleChange, setErrors }" class="!h-fit relative z-10" name="formulary">
      <FormularyForm
        :show-dropdown-active="showDropdownActive"
        :clear-formula-trigger="clear_formula_trigger"
        class="!h-fit border-2"
        :formulary="current_metric.formulary"
        :object-type="current_metric.object_type"
        :classes="!is_formula_valid && is_not_empty_formula ? 'border-1 border-red-500' : ''"
        @change="onFormulaChanged($event, handleChange, setErrors)" />
      <div class="flex relative z-1 items-end justify-start w-full">
        <span v-if="!is_formula_valid && is_not_empty_formula" class="relative bg-white inline-flex py-1 font-medium text-red-500 text-md">
          {{ $t("Formula not valid") }} <b class="ml-1"> {{ formulary_error_msg }}</b>
        </span>
        <span
          v-else-if="is_formula_valid && is_not_empty_formula"
          class="relative inline-flex py-1 font-medium text-green-500 text-md bg-white">
          {{ $t("Valid Formula") }}
        </span>
        <div class="ml-auto text-lg text-dark-brand-blue cursor-pointer bg-white" @click="clearFormula()">
          {{ $t("Reset") }}
        </div>
      </div>
    </Field>

    <div
      v-if="current_metric.formulary.expression && current_metric.id && code_view >= 5"
      class="flex flex-col items-start justify-center w-full h-full overflow-visible font-mono bg-gray-50 text-blue">
      <VAceEditor
        v-model:value="current_metric.formulary.expression"
        lang="json"
        theme="chrome"
        :style="{ height: '300px', width: '100%' }"
        :options="{
          enableBasicAutocompletion: true,
          enableLiveAutocompletion: true,
          enableSnippets: true,
          showLineNumbers: true,
          tabSize: 2
        }"
        @change="current_metric.formulary.expression = $event" />
    </div>

    <div
      v-if="current_metric.formulary.expression && current_metric.id && code_view < 5"
      class="h-[200px] w-full bg-gray-200 opacity-90 font-thin flex justify-center items-center appearance-none select-none"
      @click="code_view += 1">
      {{ $t("Computed metric Formula can't be changed after creation, if you need to update the formula create a new metric") }}
    </div>

    <div ref="wrapper" class="w-full">
      <div class="text-xl font-medium">
        {{ $t("Add to your formula") }}:
      </div>
      <div class="grid grid-cols-3 w-full">
        <div
          v-for="(item, index) in formula_items"
          :key="index"
          :style="{
            borderColor: item.color,
            color: active_item?.label == item.label ? 'white' : item.color,
            background: active_item?.label == item.label ? item.color : ''
          }"
          class="border rounded-lg mt-3 w-[96%] p-0 pr-2 mr-2 hover:shadow-md cursor-pointer"
          @click="handleClickFormulaItem(item)">
          <div class="flex items-center w-full">
            <div class="rounded-l-md h-[50px] w-10 mr-2 flex items-center justify-center" :style="{ background: item.color }">
              <div class="text-white text-xl scale-150 absolute">
                +
              </div>
            </div>
            <div>{{ item.label }}</div>
            <div
              class="ml-auto flex w-6 h-6 items-center justify-center rounded-full p-1"
              :style="{ background: active_item?.label == item.label ? '' : item.color }">
              <component :is="item.icon" :color="active_item?.label == item.label ? item.color : 'white'" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Clock9 } from "lucide-vue-next"
import FormularyForm from "@/views/dashboard/admin/metrics/formula-components/FormularyHolder.vue"
import TextInput from "@/components/shared/TextInput.vue"
import TextArea from "@/components/shared/TextArea.vue"
import { utils } from "@/store/helpers"
import { Field } from "vee-validate"
import ComboBoxInput from "@/components/shared/ComboBoxInput.vue"
import dataService from "@/services/data.service"
import { VAceEditor } from "vue3-ace-editor"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import FormulaOperatorIcon from "@/components/shared/icons/FormulaOperatorIcon.vue"
import FormulaFunctionIcon from "@/components/shared/icons/FormulaFunctionIcon.vue"
import FormulaMetricsIcon from "@/components/shared/icons/FormulaMetricsIcon.vue"
import FormulaForecastMetricIcon from "@/components/shared/icons/FormulaForecastMetricIcon.vue"
import FormulaActivityMetricIcon from "@/components/shared/icons/FormulaActivityMetricIcon.vue"
import FormulaPlatformMetricIcon from "@/components/shared/icons/FormulaPlatformMetricIcon.vue"
import FormulaNestedMetricIcon from "@/components/shared/icons/FormulaNestedMetricIcon.vue"

// import { Formulary } from "@/models/metric"

export default {
  components: {
    FormulaOperatorIcon,
    FormulaFunctionIcon,
    FormulaMetricsIcon,
    FormulaForecastMetricIcon,
    FormulaActivityMetricIcon,
    Clock9,
    FormulaPlatformMetricIcon,
    FormulaNestedMetricIcon,
    VAceEditor,
    ComboBoxInput,
    Field,
    TextInput,
    TextArea,
    FormularyForm
  },
  props: {
    metric: {
      type: Object,
      required: true
    },
    showDropdownActive: null
  },
  emits: ["update"],
  data() {
    return {
      setFormulaErrors: () => {},
      code_view: 0,
      clear_formula_trigger: 0,
      is_not_empty_formula: false,
      is_formula_valid: false,
      formulary_error_msg: "",
      current_metric: null,
      active_item: null,
      stop_formula_change: false
    }
  },

  computed: {
    formula_items() {
      return [
        {
          label: this.$t("Operators"),
          value: "operator",
          scrollbar_color: "operator-scrollbar",
          tailwind_hover_color: "hover:bg-[#fca7ac]",
          tailwind_color: "bg-[#fca7ac]",
          color: "#FF767E",
          icon: "FormulaOperatorIcon"
        },
        {
          label: this.$t("Functions"),
          value: "function",
          scrollbar_color: "function-scrollbar",
          tailwind_hover_color: "hover:bg-[#60b5d6]",
          tailwind_color: "bg-[#60b5d6]",
          color: "#2DA7D7",
          icon: "FormulaFunctionIcon"
        },
        {
          label: this.$t("Metrics"),
          value: "metric",
          scrollbar_color: "metric-scrollbar",
          tailwind_hover_color: "hover:bg-[#b34085]",
          tailwind_color: "bg-[#b34085]",
          color: "#B91979",
          icon: "FormulaMetricsIcon"
        },
        {
          label: this.$t("Forecast Metrics"),
          value: "forecast_metric",
          filter_field: "forecast_metrics",
          tailwind_hover_color: "hover:bg-[#bd93ed]",
          tailwind_color: "bg-[#bd93ed]",
          scrollbar_color: "forecastmetric-scrollbar",
          color: "#B171F9",
          icon: "FormulaForecastMetricIcon"
        },
        {
          label: this.$t("Activity Metrics"),
          value: "activity_metric",
          filter_field: "other_metrics",
          tailwind_hover_color: "hover:bg-[#79dbb2]",
          tailwind_color: "bg-[#79dbb2]",
          scrollbar_color: "activitymetric-scrollbar",
          color: "#27C884",
          icon: "FormulaActivityMetricIcon"
        },
        {
          label: this.$t("Date Extractors"),
          value: "date_extractor",
          tailwind_hover_color: "hover:bg-[#f2c961]",
          tailwind_color: "bg-[#f2c961]",
          scrollbar_color: "dataextractor-scrollbar",
          color: "#FFBE15",
          icon: "Clock9"
        },
        {
          label: this.$t("Platform Metrics"),
          value: "platform_metric",
          tailwind_hover_color: "hover:bg-[#254c5c]",
          tailwind_color: "bg-[#254c5c]",
          scrollbar_color: "platformmetric-scrollbar",
          color: "#07455D",
          icon: "FormulaPlatformMetricIcon"
        },
        {
          label: this.$t("Nested Metrics"),
          value: "nested_metric",
          tailwind_hover_color: "hover:bg-[#153dcf]",
          tailwind_color: "bg-[#153dcf]",
          scrollbar_color: "platformmetric-scrollbar",
          color: "#2645B4",
          icon: "FormulaNestedMetricIcon"
        }
      ]
    }
  },

  watch: {
    "current_metric.name": {
      deep: true,
      handler() {
        this.$utils.debounce(() => {
          this.$emit("update", this.current_metric)
        }, 200)
      }
    },

    "current_metric.metric_tag": {
      deep: true,
      handler() {
        this.$utils.debounce(() => {
          this.$emit("update", this.current_metric)
        }, 200)
      }
    },
    "current_metric.description": {
      deep: true,
      handler() {
        this.$utils.debounce(() => {
          this.$emit("update", this.current_metric)
        }, 200)
      }
    },
    "current_metric.formulary": {
      deep: true,
      handler() {
        this.$utils.debounce(() => {
          if (this.current_metric) {
            this.$emit("update", this.current_metric)
            if (this.current_metric.formulary?.expression) {
              this.is_not_empty_formula = this.current_metric.formulary?.expression?.length > 0
            } else if (this.current_metric.formulary?.cases?.length > 0) {
              this.is_not_empty_formula = this.current_metric.formulary?.cases[0]["when"].length > 0
            } else {
              this.is_not_empty_formula = false
            }

            dataService
              .isFormulaValid({
                ...this.current_metric,
                formulary: { ...this.current_metric.formulary, expression: this.parseForServer(this.current_metric?.formulary?.expression) }
              })
              .then((res) => {
                this.is_formula_valid = res.data.status == "FORMULA_IS_VALID"
                if (this.is_formula_valid && this.is_not_empty_formula) {
                  this.setFormulaErrors()
                } else {
                  this.setFormulaErrors(this.$t("Formula is not Valid"))
                }
              })
              .catch(() => {
                this.setFormulaErrors(this.$t("Formula is not Valid"))
                this.is_formula_valid = false
              })
          }
        }, 200)
      }
    }
  },

  created() {
    this.register_listeners()
    setTimeout(() => {
      this.$refs.wrapper.focus()
    }, 200)
    this.current_metric = utils.clone(this.metric)

    this.stop_formula_change = true

    setTimeout(() => {
      this.stop_formula_change = false
    }, 500)
  },

  methods: {
    clearFormula() {
      this.current_metric.formulary = { expression: [], type: "value" }
      this.clear_formula_trigger += 1
    },
    parseForServer(exp) {
      let list = []

      if (Array.isArray(exp)) {
        exp.forEach((v, index) => {
          if (exp[index]?.type == "input") {
            if (exp[index]?.data_type == "Boolean") {
              list.push(v.value ? "True" : "False")
            } else if (exp[index].data_type == "List") {
              list.push("[")
              if (v.value) {
                list.push(v.value.map((x) => `'${x}'`).join(","))
              }
              list.push("]")
            } else if (utils.isNumber(exp[index].value)) {
              list.push(String(v.value).replace(",", ""))
            } else {
              if (v?.display == "In Date Range") {
                list.push("(")
                list.push(`'${v.value.start?.toISOString()}'`)
                list.push(",")
                list.push(`'${v.value.end?.toISOString()}'`)
                list.push(")")
              } else if (v?.display == "In Range") {
                list.push("(")
                list.push(String(v.value.start).replace(",", ""))
                list.push(",")
                list.push(String(v.value.end).replace(",", ""))
                list.push(")")
              } else if (
                exp[index - 1]?.type == "operator" &&
                (exp[index - 2]?.value == "metric" || exp[index - 2]?.value == "forecast_metrics") &&
                (exp[index - 2]?.data_type == "Date" || exp[index - 2]?.data_type == "DateTime") &&
                exp[index - 1]?.value == "=="
              ) {
                list.push(`'${v.value.toISOString()}'`)
              } else if (
                exp[index - 1]?.type == "operator" &&
                (exp[index - 2]?.value == "metric" || exp[index - 2]?.value == "forecast_metrics") &&
                exp[index - 2]?.data_type == "Boolean"
              ) {
                list.push(v.value ? "True" : "False")
              } else if (
                exp[index - 1]?.type == "operator" &&
                exp[index - 1]?.value == "in" &&
                (exp[index - 2]?.value == "metric" || exp[index - 2]?.value == "forecast_metrics") &&
                exp[index - 2]?.data_type == "Number"
              ) {
                list.push("[")
                if (v.value) {
                  list.push(v.value.join(","))
                }
                list.push("]")
              } else if (
                exp[index - 1]?.type == "operator" &&
                exp[index - 1]?.value == "in" &&
                (exp[index - 2]?.value == "metric" || exp[index - 2]?.value == "forecast_metrics") &&
                exp[index - 2]?.data_type == "String"
              ) {
                list.push("[")
                if (v.value) {
                  list.push(
                    v.value
                      .split(",")
                      .map((x) => `'${x}'`)
                      .join(",")
                  )
                }
                list.push("]")
              } else {
                list.push(`'${v.value}'`)
              }
            }
          } else {
            if (v) {
              list.push(v?.value)
            }
          }
        })
        return list.join(" ")
      } else {
        return exp
      }
    },
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.CLEAR_ACTIVE_FORMULA_ITEM, () => {
        this.active_item = null
      })
    },
    onFormulaChanged($event, handleChange, setErrors) {
      if (!this.stop_formula_change) {
        if (typeof $event?.expression == "string") {
          this.setFormulaErrors = setErrors

          const obj_calc = this.current_metric.formulary

          if (!obj_calc.type) {
            obj_calc["type"] = "value"
          }
          this.current_metric.formulary = obj_calc
        }

        if ($event?.cases) {
          if (this.current_metric.formulary) {
            this.current_metric.formulary = $event
          } else {
            this.current_metric["formulary"] = $event
          }
        } else {
          const obj_calc = this.$utils.clone($event)
          if (obj_calc.expression.length && Array.isArray(obj_calc.expression)) {
            let string_calc = ""
            obj_calc.expression.forEach((char) => {
              if (char.display) {
                if (string_calc) {
                  string_calc += " "
                }
                string_calc += char.display
              } else if (char.reference_name) {
                if (string_calc) {
                  string_calc += " "
                }
                string_calc += char.reference_name
              }
            })
          }
          this.current_metric.formulary = $event
        }
      }
    },

    handleClickFormulaItem(item) {
      if (item.label == this.active_item?.label) {
        this.active_item = null
      } else {
        this.active_item = item
      }
      this.$emitter.emit(ALL_EMITTERS.CLICKFORMULAITEM, this.active_item)
    }
  }
}
</script>
